{
  "title": "Configuration",
  "subtitle": "The lists and rules that shape how the app behaves. Managers can read them, only admins can change them.",
  "savedRules": "Settings saved",
  "nav": {
    "label": "Configuration sections",
    "rules": "Rules",
    "lists": "Lists",
    "sales": "Sales",
    "stock-rules": "Stock rules",
    "alert-settings": "Alert settings",
    "document-numbers": "Document numbers",
    "document-print": "Printed documents",
    "labels": "Barcode labels",
    "languages": "Languages",
    "tax-rates": "VAT rates",
    "price-lists": "Price lists",
    "channels": "Sales channels",
    "carriers": "Carriers",
    "units": "Units of measure",
    "adjustment-reasons": "Adjustment reasons",
    "options": "Product options",
    "attributes": "Product attributes",
    "sales-rules": "Sales rules",
    "cash-registers": "Cash registers",
    "online-store": "Online store",
    "supplier-payment-methods": "Supplier payment methods"
  },
  "stockRules": {
    "title": "Stock rules",
    "negativeQuestion": "When an operation would make stock negative",
    "recommended": "recommended",
    "allow": {
      "title": "Allow, after confirmation",
      "text": "The person must confirm, it is logged and managers get an alert."
    },
    "block": {
      "title": "Block",
      "text": "The operation cannot be validated until stock is enough."
    },
    "quickTransfers": {
      "title": "Quick transfers",
      "text": "Allow one-step transfers between sites for people with the “Quick transfer” permission."
    },
    "threshold": {
      "title": "Corrections that need a manager",
      "text": "Adjustments worth more than this wait for a manager to approve them."
    },
    "costing": {
      "title": "Costing method",
      "text": "Each product has one average cost, updated at every stock in. Fixed in this version.",
      "value": "Weighted average"
    }
  },
  "alerts": {
    "title": "Alert settings",
    "subtitle": "Used by the alert engine of phase 5.",
    "deadStock": "Dead stock after",
    "transferLate": "Transfer is late after",
    "countVariance": "Count difference alert above",
    "digest": "Daily summary email at"
  },
  "numbers": {
    "title": "Document numbers",
    "subtitle": "Numbers follow PREFIX-YEAR-COUNTER; the counter starts again every year.",
    "operation": "Operation",
    "prefix": "Prefix",
    "nextCounter": "Next counter",
    "nextNumber": "Next number will be",
    "prefixOf": "Prefix of {{type}}",
    "saved": "Prefix saved",
    "transactionNote": "Activity log numbers ({{example}}) are fixed: they identify records that never change.",
    "types": {
      "stockIn": "Stock in",
      "stockOut": "Stock out",
      "adjustment": "Adjustment",
      "openingStock": "Opening stock",
      "transfer": "Transfer",
      "count": "Inventory count",
      "transaction": "Activity log",
      "productImport": "Product import",
      "invoice": "Invoice",
      "creditNote": "Credit note",
      "supplierPayment": "Supplier payment"
    }
  },
  "languages": {
    "title": "Languages",
    "subtitle": "Languages people can pick for the interface.",
    "default": "Default",
    "note": "The default language, chosen in the company profile, always stays enabled."
  },
  "taxRates": {
    "title": "VAT rates",
    "subtitle": "Each product has a VAT rate; prices are entered tax included and receipts show the VAT per rate.",
    "add": "Add rate",
    "edit": "Edit rate",
    "name": "Name",
    "rate": "Rate",
    "ratePercent": "Rate (%)",
    "rateHint": "0 for exempt products, up to {{max}}, two decimals at most.",
    "default": "Default",
    "makeDefault": "Make default",
    "madeDefault": "{{name}} is now the default rate",
    "empty": "No VAT rate yet. Add the rates you invoice, for example 19%, 7% and exempt."
  },
  "priceLists": {
    "title": "Price lists",
    "subtitle": "One list for the counters, one for the online store, one for wholesale sales. Prices are tax included.",
    "name": "Price list",
    "prices": "Prices",
    "channels": "Used by",
    "base": "The selling price of each product",
    "ownOrBase": "Its own price, else the selling price",
    "none": "No channel",
    "rename": "Rename",
    "renameTitle": "Rename the price list",
    "rule": "A product without an online price sells online at its selling price. A product without a wholesale price is not sold in wholesale mode. Set these prices on the product page.",
    "ownOnly": "Its own price only: without one, not sold at wholesale"
  },
  "channels": {
    "title": "Sales channels",
    "subtitle": "Where sales happen: each store counter, the online store and the back office.",
    "channel": "Channel",
    "numbers": "Next order",
    "priceList": "Price list",
    "payment": "Payment",
    "off": "Off",
    "atSite": "At {{name}}",
    "fromSite": "Prepared at {{name}}",
    "noFulfillmentSite": "No fulfillment site yet",
    "anySite": "Site chosen per order",
    "withCarrier": "ships with {{name}}",
    "rule": "Each store gets its counter channel when it is created. The online store opens once it has a fulfillment site.",
    "editTitle": "Edit {{name}}",
    "typeHints": {
      "pos": "Sales at the counter of this store, paid on the spot.",
      "online": "Orders from the online store, prepared at one site, shipped or collected.",
      "backOffice": "Orders taken by phone or by the team, for any site."
    },
    "activeLabel": {
      "pos": "Sell at this counter",
      "online": "Take online orders",
      "backOffice": "Take back-office orders"
    },
    "activeHint": {
      "pos": "Off: the cash registers of this store cannot sell.",
      "online": "Needs a fulfillment site and a way to deliver.",
      "backOffice": "Off: the team cannot create orders."
    },
    "name": "Name",
    "prefix": "Order prefix",
    "prefixHint": "Orders are numbered {{example}}.",
    "priceListHint": "The prices this channel sells at.",
    "paymentMethods": "Payment methods",
    "onlinePaymentLater": "Card payment on the website comes with a payment provider (later phase).",
    "fulfillmentSite": "Fulfillment site",
    "fulfillmentHint": "Its stock is reserved and issued for these orders.",
    "defaultSite": "Default site",
    "defaultSiteHint": "Suggested on new orders; each order can use another site.",
    "noSite": "No site",
    "deliveryModes": "Delivery",
    "deliveryHints": {
      "shipping": "Sent to the customer’s address.",
      "pickup": "Collected by the customer at a pickup site."
    },
    "pickupSites": "Pickup sites",
    "pickupHint": "Where customers may collect their orders.",
    "noSites": "No active site yet. Create your sites in Locations first.",
    "steps": "Preparation steps",
    "stepsHint": "Orders go through these steps before they are handed over.",
    "reservation": "Stock reservation",
    "reservationHint": "An unpaid order keeps its stock this long ({{min}} to {{max}} minutes).",
    "minutes": "min",
    "saved": "{{name}} saved",
    "returnDays": "Return period",
    "returnDaysHint": "Days after the hand-over during which items can come back (0 to {{max}}). A manager can accept later returns.",
    "days": "days",
    "shippingFee": "Shipping fee",
    "shippingFeeHint": "Added to delivered orders of the online store, tax included.",
    "defaultCarrier": "Default carrier",
    "defaultCarrierHint": "Orders to deliver of this channel leave with it unless another carrier is chosen.",
    "noCarriers": "No carrier yet: add them under Carriers, below.",
    "noCarrier": "None: chosen at shipping"
  },
  "lists": {
    "rule": "A value that is in use can only be deactivated. Unused values can be deleted.",
    "usage": "Used by",
    "notUsed": "Not used",
    "usedBy_one": "{{count}} product",
    "usedBy_other": "{{count}} products",
    "inactive": "Inactive",
    "system": "System",
    "activate": "Activate",
    "deactivate": "Deactivate",
    "saved": "Saved",
    "activated": "{{name}} activated",
    "deactivated": "{{name}} deactivated",
    "deleted": "{{name}} deleted",
    "confirmDeleteTitle": "Delete {{name}}?",
    "confirmDeleteText": "This value has never been used: it will be removed for good. The change stays in the activity log.",
    "confirmDeactivateTitle": "Deactivate {{name}}?",
    "confirmDeactivateText": "It stays on past records but can no longer be picked. You can activate it again later."
  },
  "units": {
    "title": "Units of measure",
    "add": "Add unit",
    "edit": "Edit unit",
    "name": "Unit",
    "symbol": "Symbol",
    "decimals": "Decimals",
    "decimalsHint": "0 for pieces, up to {{max}} for weight or length.",
    "days": "days"
  },
  "categories": {
    "title": "Categories",
    "add": "Add category",
    "edit": "Edit category",
    "name": "Category",
    "parent": "Parent category",
    "noParent": "None (top level)",
    "children": "Sub-categories",
    "empty": "No category yet.",
    "subtitle": "How products are grouped, a tree of categories and sub-categories. The online store and the reports use it.",
    "emptyHint": "Add the first category, then sub-categories under it.",
    "search": "Search a category",
    "noMatch": "No category has this name.",
    "products": "Products",
    "branch": "With sub-categories",
    "seeProducts_one": "{{count}} product",
    "seeProducts_other": "{{count}} products",
    "addSub": "Sub-category",
    "addSubFor": "Add a sub-category to {{name}}",
    "expand": "Show the sub-categories of {{name}}",
    "collapse": "Hide the sub-categories of {{name}}",
    "expandAll": "Expand all",
    "collapseAll": "Collapse all"
  },
  "brands": {
    "title": "Brands",
    "add": "Add brand",
    "edit": "Edit brand",
    "name": "Brand",
    "empty": "No brand yet.",
    "subtitle": "The brands products are made by. Products are filtered by brand in the list and the online store.",
    "emptyHint": "Add the brands you sell; a product takes one when it is created or edited.",
    "search": "Search a brand",
    "noMatch": "No brand has this name.",
    "products": "Products"
  },
  "reasons": {
    "title": "Adjustment reasons",
    "add": "Add reason",
    "edit": "Edit reason",
    "name": "Reason",
    "direction": "Direction",
    "directions": {
      "increase": "Increase",
      "decrease": "Decrease",
      "both": "Both"
    }
  },
  "options": {
    "title": "Product options",
    "subtitle": "Reusable options for products with variants: Color, Size, Material, Finish. A product picks the values it comes in.",
    "add": "Add option",
    "edit": "Edit option",
    "name": "Name",
    "presentation": "Shown as",
    "values": "Values",
    "presentations": {
      "swatch": "Colour swatches",
      "buttons": "Buttons",
      "dropdown": "Dropdown list",
      "image": "Pictures"
    },
    "presentationHints": {
      "swatch": "A colour dot for each value. Give each value its colour.",
      "buttons": "One button per value: best for sizes and short choices.",
      "dropdown": "A list to choose from: best when there are many values.",
      "image": "A picture per value (a pattern, a texture). Add pictures once the option is saved."
    },
    "codeHint": "(the code goes into variant SKUs: PNT-10L-WHT)",
    "label": "Value",
    "code": "Code",
    "color": "Colour of {{name}}",
    "active": "Active",
    "addValue": "Add",
    "quickAdd": "Add several at once: XS, S, M, L",
    "moveUp": "Move up",
    "moveDown": "Move down",
    "remove": "Remove {{name}}",
    "removeUsed_one": "Used by {{count}} variant: deactivate it instead",
    "removeUsed_other": "Used by {{count}} variants: deactivate it instead",
    "noValues": "No value yet.",
    "more": "+{{count}}",
    "addPicture": "Picture of {{name}}",
    "removePicture": "Remove the picture of {{name}}",
    "pictureLater": "Save first",
    "picturesAfterSave": "Save the option, then add a picture to each value.",
    "empty": "No option yet. Add the ones your products come in: Color, Size, Finish..."
  },
  "attributes": {
    "title": "Product attributes",
    "subtitle": "Reusable descriptions that do not create variants: Weight, Dimensions, Waterproof. They help find and compare products.",
    "add": "Add attribute",
    "edit": "Edit attribute",
    "name": "Name",
    "type": "Type",
    "details": "Unit or choices",
    "types": {
      "text": "Text",
      "number": "Number",
      "boolean": "Yes or no",
      "choice": "Choice from a list"
    },
    "unit": "Unit",
    "unitHint": "Shown after the number: kg, mm, W.",
    "choices": "Choices",
    "choicesHint": "One choice per line.",
    "empty": "No attribute yet. Add the details you want to record on products: Weight, Dimensions..."
  },
  "salesRules": {
    "title": "Sales rules",
    "subtitle": "What invoices add, how long couriers have to pay in, the discount at the till.",
    "stampDuty": "Stamp duty on invoices",
    "stampDutyHint": "Added once to each invoice, after the total incl. VAT.",
    "codDays": "Cash on delivery settlement",
    "codDaysHint": "Days after delivery before an unsettled order raises an alert.",
    "posDiscount": "Cashier discount limit",
    "posDiscountHint": "The share of a ticket a cashier may give off at the till. Beyond it, someone allowed to give large discounts makes the sale."
  },
  "cashRules": {
    "title": "Cash registers",
    "subtitle": "How drawers are counted and when shifts should be closed.",
    "denominations": "Notes and coins",
    "denominationsHint": "Those counted at the opening and the closing of a shift.",
    "denominationsRequired": "Keep at least one note or coin.",
    "varianceLimit": "Variance without approval",
    "varianceLimitHint": "A closing count further than this from the expected cash waits for a manager.",
    "closeTime": "Closing time",
    "closeTimeHint": "A shift still open after this time raises an alert.",
    "blind": "Blind closing count",
    "blindHint": "Cashiers count the drawer without seeing the expected cash; managers still see it."
  },
  "onlineStore": {
    "title": "Online store",
    "subtitle": "What customers see first: the carousel, the banners and the home page of the store. Changes reach it within a minute.",
    "carousel": {
      "title": "Carousel",
      "description": "The first thing under the store’s header: one photo at a time, with arrows and dots. Each photo leads where you choose.",
      "add": "Add a photo",
      "edit": "Edit the photo",
      "empty": "No photos yet",
      "emptyHint": "With no photo on, the store shows no carousel and starts with the blocks below."
    },
    "banners": {
      "title": "Banners",
      "description": "The store shows the ones switched on, and nothing in place of the others.",
      "deleteText": "It is removed for good, with its picture. To bring it back later, switch it off instead. The change stays in the activity log.",
      "add": "Add a banner",
      "edit": "Edit the banner",
      "empty": "No banners yet",
      "emptyHint": "Until you add one, the top of the home page shows your newest products, your offers and your categories.",
      "scheduled": "Scheduled",
      "ended": "Ended",
      "from": "From {{from}}",
      "until": "Until {{to}}",
      "between": "{{from}} to {{to}}"
    },
    "placements": {
      "announcement": "Top bar",
      "carousel": "Carousel",
      "hero": "Main banner",
      "tile": "Side banner"
    },
    "placementHints": {
      "announcement": "A line above every page. Only the first active one shows.",
      "carousel": "The slideshow right under the header: one photo at a time.",
      "hero": "The large picture of the home page. Several take turns.",
      "tile": "The two smaller pictures beside it. Without them, the main banner takes the whole width."
    },
    "form": {
      "placement": "Where it shows",
      "title": "Title",
      "text": "Text",
      "action": "Button",
      "actionHint": "A few words, such as “See the collection”.",
      "language": "Language",
      "allLanguages": "All languages",
      "picture": "Picture",
      "pictureHint": "JPEG, PNG or WebP, up to 5 MB, wide (1600 × 900). The title goes over it: on phones its sides are cut.",
      "photo": "Photo",
      "photoRequired": "Choose a photo.",
      "description": "Description",
      "descriptionHint": "What the photo shows, for screen readers and search engines.",
      "caption": "Caption",
      "captionHint": "Written on the photo. Leave it empty if the photo has its own words.",
      "link": "Opens",
      "noLink": "Nothing",
      "category": "Category",
      "brand": "Brand",
      "url": "Web address",
      "urlHint": "A page of the store without its language (/products/robe-lin), or a whole address (https://…).",
      "startsOn": "First day",
      "endsOn": "Last day",
      "datesHint": "Empty: from now on, with no end.",
      "photoHint": "JPEG, PNG or WebP, up to 5 MB, very wide (2100 × 900). On phones its sides are cut.",
      "backgroundColor": "Background colour",
      "textColor": "Text colour",
      "barPreview": "Your line of text"
    },
    "links": {
      "products": "All products",
      "newest": "New products",
      "offers": "Offers",
      "category": "A category",
      "brand": "A brand",
      "url": "A web address"
    },
    "home": {
      "title": "Home page",
      "description": "The blocks under the carousel, in this order.",
      "show": "Show {{name}}",
      "addCategory": "Add the products of a category",
      "add": "Add",
      "chooseCategory": "Choose a category",
      "categorySection": "Products: {{name}}",
      "unknownCategory": "A category that no longer exists",
      "remove": "Remove {{name}}",
      "saved": "Home page saved",
      "featuredAuto": "Automatic: the first products of the catalog",
      "featuredChosen_one": "{{count}} product chosen",
      "featuredChosen_other": "{{count}} products chosen",
      "choose": "Choose products"
    },
    "featured": {
      "description": "Up to {{max}} products, in the order the store shows them. With none, it shows the first products of the catalog. Save the home page to publish your choice.",
      "none": "None chosen: the store shows the first products of the catalog.",
      "find": "Find a product by name or code",
      "noMatch": "No product of the store matches.",
      "full": "{{max}} products at most: remove one to add another.",
      "notInStore": "Not in the store",
      "add": "Add {{name}}",
      "remove": "Remove {{name}}"
    },
    "sections": {
      "hero": "Banners",
      "categories": "Categories",
      "featured": "Featured products",
      "newest": "New products",
      "offers": "Offers",
      "packs": "Packs",
      "category": "Products of a category",
      "brands": "Brands",
      "assurances": "Delivery and payment"
    }
  },
  "storeWebsites": {
    "title": "Online store",
    "subtitle": "The websites of your online store, allowed to use the store API from a browser.",
    "none": "No website yet: only the back office and servers can call the store API.",
    "add": "Website",
    "addHint": "Its address without a path, like https://shop.example.tn.",
    "addButton": "Add",
    "remove": "Remove",
    "apiHint": "The store API is described in docs/store-api.openapi.json (served at /store/v1)."
  },
  "supplierMethods": {
    "title": "Supplier payment methods",
    "description": "How suppliers are paid. The first five are built in and can be renamed; add your own (a card, a mobile wallet).",
    "add": "Add method",
    "edit": "Edit method",
    "name": "Method",
    "kind": "Works as",
    "kinds": {
      "cash": "Cash",
      "transfer": "Bank transfer",
      "cheque": "Cheque: number, bank, due date",
      "kembiala": "Bill of exchange: number, bank, due date",
      "deferred": "Credit: a promise until paid",
      "other": "Other"
    },
    "usedBy_one": "{{count}} payment",
    "usedBy_other": "{{count}} payments"
  },
  "carriers": {
    "title": "Carriers",
    "description": "The delivery companies that take your parcels. They are picked on orders to deliver, on the sales channels and on the courier settlements.",
    "add": "Add carrier",
    "edit": "Edit carrier",
    "name": "Carrier",
    "phone": "Phone",
    "tracking": "Tracking",
    "hasTracking": "Tracking link",
    "trackingUrl": "Tracking link",
    "trackingHint": "The carrier's parcel page, with {tracking} where the tracking number goes, for example https://carrier.tn/track?n={tracking}. Orders and customers' emails then link to it.",
    "cash": "Cash on delivery",
    "collects": "Collects",
    "noCash": "Does not collect",
    "cashOnDelivery": "Collects cash on delivery",
    "cashOnDeliveryHint": "It brings back the cash of the orders it delivers: it is offered when a courier hands cash in.",
    "usedBy_one": "{{count}} order or channel",
    "usedBy_other": "{{count}} orders and channels"
  },
  "labelSettings": {
    "title": "Barcode labels",
    "subtitle": "The size and content of the labels printed from products, receipts and transfers. Each print can change them.",
    "preview": "Preview",
    "sampleName": "Classic abaya",
    "sampleOptions": "S / Black",
    "invalid": "Check the format: labels of 15 to 150 mm, up to 8 columns and 30 rows.",
    "where": "Print them from a product (\"Print labels\"), a validated receipt or a received transfer. Thermal printers: set the driver's paper to the label size."
  },
  "catalogStore": {
    "photo": "Photo for the online store",
    "photoHint": "Shown on the store's category page. JPEG, PNG or WebP, up to 5 MB; a square or landscape picture works best.",
    "logo": "Logo",
    "logoHint": "Shown in the store's brands. JPEG, PNG or WebP, up to 5 MB, on a light background.",
    "choose": "Choose a picture",
    "change": "Change",
    "remove": "Remove",
    "description": "Description",
    "descriptionHint": "A few lines the online store shows with it.",
    "website": "Website",
    "slug": "Address",
    "slugHint": "The end of its page address on the store. Changed, the old address leads to the new one.",
    "slugAuto": "Empty: made from the name.",
    "shownOnline": "Shown in the online store",
    "categoryOnlineHint": "Off: the store's categories leave it out, with the categories below it. Its products stay on sale.",
    "brandOnlineHint": "Off: the store's brands leave it out. Its products stay on sale.",
    "notOnline": "Not online",
    "up": "Move {{name}} up",
    "down": "Move {{name}} down",
    "seo": {
      "title": "Search and sharing",
      "hint": "What Google and a share show of its page. Empty: its name, description and photo.",
      "seoTitle": "Title for search and sharing",
      "seoTitleHint": "Empty: its name.",
      "seoDescription": "Description for search and sharing",
      "seoDescriptionHint": "Empty: its description.",
      "socialImage": "Sharing image",
      "socialImageHint": "Empty: its photo. 1200 × 630 px fits best."
    }
  },
  "documentPrint": {
    "title": "Printed documents",
    "subtitle": "What invoices, credit notes and delivery notes show by default. Each print can change it. What the law asks of an invoice always prints: its number and date, both parties and their tax IDs, quantities, amounts and VAT."
  },
  "seo": {
    "title": "Search & social",
    "subtitle": "How the online store shows in Google and when its pages are shared on Facebook, Instagram or WhatsApp. Products, categories and brands have their own texts in their forms.",
    "copy": "Copy",
    "copied": "Copied.",
    "save": "Save",
    "saved": "Search & social saved.",
    "fixErrors": "Some values need checking.",
    "imageSaved": "Image saved.",
    "texts": {
      "title": "Texts and sharing images",
      "hint": "What a result and a share say. Leave empty: the page’s own words, then the defaults.",
      "page": "Page",
      "language": "Language",
      "defaultTitleHint": "The home page’s title; empty: the shop’s name.",
      "pageTitleHint": "Empty: the page’s own name.",
      "defaultDescriptionHint": "For every page that has no text of its own.",
      "pageDescriptionHint": "Empty: the default description."
    },
    "pages": {
      "default": "Every page",
      "home": "Home",
      "products": "All products",
      "offers": "Offers",
      "packs": "Packs"
    },
    "fields": {
      "title": "Title",
      "description": "Description"
    },
    "image": {
      "default": "Default sharing image",
      "defaultHint": "For pages without a picture of their own. Without one, the logo. 1200 × 630 px fits best.",
      "page": "Sharing image of this page",
      "pageHint": "Empty: the default sharing image. Saved as soon as it is chosen."
    },
    "preview": {
      "label": "Preview",
      "google": "Google",
      "social": "Facebook, Instagram, WhatsApp",
      "noDescription": "No description: Google writes one from the page.",
      "noImage": "No picture: the share shows text only."
    },
    "search": {
      "title": "Search engines",
      "hint": "Google Search Console and Bing Webmaster Tools.",
      "indexing": "Let search engines index the store",
      "indexingHint": "Off for a test server: robots.txt closes the site and every page says noindex.",
      "closed": "Search engines are asked to stay out of the whole store.",
      "google": "Google verification code",
      "googleHint": "Search Console › Add a property › HTML tag: paste the code or the whole tag.",
      "bing": "Bing verification code",
      "bingHint": "Bing Webmaster Tools › HTML meta tag.",
      "sitemap": "Sitemap",
      "sitemapHint": "Give this address in Search Console › Sitemaps.",
      "googleFeed": "Product feed for Google Merchant Center",
      "googleFeedHint": "Products › Feeds › Add a feed › Scheduled fetch: one item per variant, updated every 15 minutes."
    },
    "meta": {
      "title": "Meta: Facebook and Instagram",
      "hint": "The pixel measures visits and sales of your ads; the catalog feeds Shops and dynamic ads.",
      "pixel": "Pixel (dataset) ID",
      "pixelHint": "Events Manager › the dataset › Settings: its ID, digits only.",
      "domain": "Domain verification code",
      "domainHint": "Business settings › Brand safety › Domains › Meta tag: the code or the whole tag.",
      "consent": "Ask visitors before the pixel loads",
      "consentHint": "A bar asks first; nothing of Meta loads before Accept. Keep it on unless your lawyer says otherwise.",
      "capi": "Conversions API",
      "capiHint": "Sends each online order from the server too, so blocked browsers still count. Set META_CAPI_TOKEN on the server to turn it on.",
      "capiOn": "On",
      "capiOff": "Off",
      "feed": "Catalog feed for Commerce Manager",
      "feedHint": "Catalog › Data sources › Data feed › Scheduled feed."
    },
    "social": {
      "title": "Social profiles",
      "hint": "Your pages, so Google links them to the shop.",
      "facebook": "Facebook",
      "instagram": "Instagram",
      "tiktok": "TikTok",
      "youtube": "YouTube"
    }
  }
}
